import { Play, Pause, RotateCcw, RotateCw, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useDailyRecapPlayer } from "@/context/DailyRecapPlayerContext";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RecapCoverCycler } from "./RecapStoryStrip";
import { useT } from "@/i18n/useLocale";
import T from "@/i18n/T";

interface FullDailyRecapPlayerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FullDailyRecapPlayer = ({ isOpen, onClose }: FullDailyRecapPlayerProps) => {
  const t = useT();
  const {
    currentRecap,
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    autoPlayEnabled,
    pause,
    resume,
    seek,
    setVolume,
    setPlaybackRate,
    toggleAutoPlay,
  } = useDailyRecapPlayer();

  if (!currentRecap) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const recapDate = format(new Date(currentRecap.date), "EEEE, MMMM d, yyyy");

  const handleTogglePlay = () => {
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  };

  const cyclePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
  };


  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[90vh] p-0 bg-background">
        <div className="flex flex-col h-full bg-background">
          {/* Drag handle */}
          <div className="flex justify-center pt-4 pb-2">
            <div className="w-12 h-1 rounded-full bg-border" />
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="max-w-md mx-auto space-y-6 mt-4">
              {/* Artwork */}
              <div className="relative aspect-square overflow-hidden rounded-2xl bg-gradient-to-br from-primary/30 via-primary/20 to-primary/10 flex items-center justify-center shadow-2xl">
                <RecapCoverCycler
                  articleIds={currentRecap.article_ids}
                  imageSize={700}
                  className="absolute inset-0 h-full w-full"
                />
                <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-sm font-medium text-foreground/80 bg-background/70 backdrop-blur-sm rounded-full px-3 py-1">
                  {currentRecap.article_count} articles
                </p>
              </div>

              {/* Info */}
              <div className="text-center space-y-1">
                <h3 className="text-xl font-bold text-foreground">
                  <T k="auto.daily-tech-recap-f2ce" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  {recapDate}
                </p>
              </div>

              {/* Progress */}
              <div className="space-y-2">
                <Slider
                  value={[progress]}
                  max={100}
                  step={0.1}
                  onValueChange={([value]) => {
                    const newTime = (value / 100) * duration;
                    seek(newTime);
                  }}
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Main Controls */}
              <div className="flex items-center justify-center gap-6 mb-8">
                <button
                  onClick={() => seek(Math.max(0, currentTime - 15))}
                  className="w-12 h-12 rounded-full hover:bg-muted transition-colors flex items-center justify-center group"
                  aria-label={t("auto.skip-back-15-seconds-3196")}
                >
                  <RotateCcw className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
                  <span className="absolute text-[10px] font-bold mt-1">15</span>
                </button>
                
                <button
                  onClick={handleTogglePlay}
                  className="w-16 h-16 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg hover:scale-105 flex items-center justify-center"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <Pause className="h-8 w-8 fill-primary-foreground" />
                  ) : (
                    <Play className="h-8 w-8 ml-1 fill-primary-foreground" />
                  )}
                </button>
                
                <button
                  onClick={() => seek(Math.min(duration, currentTime + 15))}
                  className="w-12 h-12 rounded-full hover:bg-muted transition-colors flex items-center justify-center group"
                  aria-label={t("auto.skip-forward-15-seconds-087f")}
                >
                  <RotateCw className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
                  <span className="absolute text-[10px] font-bold mt-1">15</span>
                </button>
              </div>

              {/* Secondary controls */}
              <div className="flex items-center justify-between px-4">
                {/* Volume */}
                <div className="flex items-center gap-2 flex-1 max-w-[120px]">
                  <Volume2 className="h-5 w-5 text-muted-foreground" />
                  <Slider
                    value={[volume * 100]}
                    max={100}
                    step={1}
                    onValueChange={([value]) => setVolume(value / 100)}
                    className="flex-1"
                  />
                </div>

                {/* Playback Speed */}
                <button
                  onClick={cyclePlaybackRate}
                  className="px-4 py-2 rounded-full bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
                >
                  {playbackRate}x
                </button>

              </div>

              {/* Auto-play */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <Label htmlFor="autoplay" className="cursor-pointer">
                  <T k="auto.auto-play-next-recap-291d" />
                </Label>
                <Switch
                  id="autoplay"
                  checked={autoPlayEnabled}
                  onCheckedChange={toggleAutoPlay}
                />
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
